import { ArrowLeft, CheckCircle } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { apiClient } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface MappingCounts {
  contact: number;
  deal: number;
  total: number;
}


const IntegrationComplete: FC = () => {
  const navigate = useNavigate();
  const { portalId, encryptedUserId, isLoading: authLoading, error: authError } = useAuth();
  const [mappingCounts, setMappingCounts] = useState<MappingCounts>({
    contact: 0,
    deal: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMappingCounts = async () => {
      if (authLoading) {
        return;
      }

      if (!portalId) {
        setError(authError || 'Portal ID not found');
        setLoading(false);
        return;
      }

      try {
        if (!encryptedUserId) {
          setError('User ID not found');
          setLoading(false);
          return;
        }

        const response = await apiClient.getMappingCounts(portalId, encryptedUserId);

        if (response.success) {
          setMappingCounts(response.counts);
        } else {
          setError(response.error || 'Failed to fetch mapping counts');
        }
      } catch (err) {
        setError('Failed to fetch mapping counts');
      } finally {
        setLoading(false);
      }
    };

    fetchMappingCounts();
  }, [portalId, encryptedUserId, authLoading, authError]);

  const handleGoToHubSpot = () => {
    // Construct dynamic HubSpot URL with user's portal ID
    if (portalId) {
      const hubspotUrl = `https://app.hubspot.com/contacts/${portalId}/objects/0-1/views/all/list`;
      window.open(hubspotUrl, '_blank');
    } else {
      // Fallback to generic HubSpot URL if portal ID is not available
      window.open('https://app.hubspot.com/', '_blank');
    }
  };

  const handleBackToSync = () => {
    if (encryptedUserId) {
      // Navigate back to the sync rule configuration page with userId
      navigate(
        `/sync-rule-configuration?userId=${encodeURIComponent(encryptedUserId)}`
      );
    } else {
      // Fallback to landing page if no userId found
      navigate('/');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Integration Complete!
        </h2>
        <p className="text-gray-600 mb-8">
          Your HubSpot and Veracross systems are now connected and ready to
          sync.
        </p>

        {/* Quick Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-8">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-900">Contact Mappings</p>
              <p className="text-gray-600">
                {loading
                  ? 'Loading...'
                  : error
                    ? 'Error'
                    : `${mappingCounts.contact} fields`}
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-900">Deal Mappings</p>
              <p className="text-gray-600">
                {loading
                  ? 'Loading...'
                  : error
                    ? 'Error'
                    : `${mappingCounts.deal} fields`}
              </p>
            </div>
          </div>
          {!loading && !error && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="font-medium text-gray-900">Total Mappings</p>
              <p className="text-gray-600">{mappingCounts.total} fields</p>
            </div>
          )}
          {error && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Information Panel */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-2">Important Information</h4>
          <p className="text-sm text-blue-700">
            Past records won't be synced. Ongoing updates will sync normally.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <Button
            onClick={handleGoToHubSpot}
            className="w-full bg-[#ff7a59] hover:bg-[#e66a4a] text-white border-[#ff7a59]"
          >
            Go to HubSpot
          </Button>
          <Button
            variant="outline"
            onClick={handleBackToSync}
            className="w-full flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Configure Sync
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationComplete;
